/**
 * useTripAnalysis
 * Hämtar registrerade trippar (MongoDB) och bin-fil-analys för admin-modulen "Analysera Trippar".
 */
import { useCallback, useState } from 'react';
import i18n from '@/i18n/config';
import { tripsService } from '@/services/trips/tripsService';
import { tripAnalysisService, BinAnalysisResult } from '@/services/tripAnalysis/tripAnalysisService';
import type { PanelTrip } from '@/components/trips/TripListPanel';

/** Sortera trippar nyast först (samma ordning på registrerade och bin-listan). */
const byStartTimeDesc = (a: PanelTrip, b: PanelTrip): number =>
  new Date(b.startTime).getTime() - new Date(a.startTime).getTime();

export interface UseTripAnalysisResult {
  registeredTrips: PanelTrip[];
  binTrips: PanelTrip[];
  binResult: BinAnalysisResult | null;
  loadingRegistered: boolean;
  loadingBin: boolean;
  error: string | null;
  binError: string | null;
  loadRegistered: (vehicleId: string, from: string, to: string) => Promise<void>;
  loadBin: (vehicleId: string, from: string, to: string) => Promise<void>;
  runReplay: (vehicleId: string, from: string, to: string) => Promise<{ id?: string; status?: string }>;
  reset: () => void;
}

export function useTripAnalysis(): UseTripAnalysisResult {
  const [registeredTrips, setRegisteredTrips] = useState<PanelTrip[]>([]);
  const [binTrips, setBinTrips] = useState<PanelTrip[]>([]);
  const [binResult, setBinResult] = useState<BinAnalysisResult | null>(null);
  const [loadingRegistered, setLoadingRegistered] = useState(false);
  const [loadingBin, setLoadingBin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [binError, setBinError] = useState<string | null>(null);

  const loadRegistered = useCallback(async (vehicleId: string, from: string, to: string) => {
    setLoadingRegistered(true);
    setError(null);
    try {
      const { trips } = await tripsService.getTrips({
        vehicleId,
        limit: 500,
        status: 'completed',
        from: `${from}T00:00:00.000Z`,
        to: `${to}T23:59:59.999Z`,
      });
      setRegisteredTrips(
        trips
          .map((t) => ({
            id: t.tripId,
            startTime: t.startTime,
            endTime: t.endTime,
            distanceM: t.distance,
            durationSec: t.duration,
            averageSpeed: t.averageSpeed,
            startAddress: t.startAddress,
            endAddress: t.endAddress,
            pointCount: t.routePointCount,
            isPrivate: t.isPrivate,
            status: t.status,
            reconstructed: t.reconstructed,
            distanceSource: t.distanceSource,
          }))
          .sort(byStartTimeDesc)
      );
    } catch (e: any) {
      setError(e?.message || i18n.t('copy.hooks.registeredTripsFetchFailed'));
      setRegisteredTrips([]);
    } finally {
      setLoadingRegistered(false);
    }
  }, []);

  const loadBin = useCallback(async (vehicleId: string, from: string, to: string) => {
    setLoadingBin(true);
    setBinError(null);
    try {
      const result = await tripAnalysisService.getBinAnalysis({ vehicleId, from, to });
      setBinResult(result);
      setBinTrips(
        (result.allTrips || [])
          .map((t, i) => ({
            id: `bin-${i}-${t.start}`,
            startTime: t.start,
            endTime: t.end,
            distanceM: t.distanceM,
            durationSec: Math.round((new Date(t.end).getTime() - new Date(t.start).getTime()) / 1000),
            maxSpeedKmh: t.maxSpeedKmh,
            pointCount: t.points,
            hasAvl250: t.hasAvl250,
            reconstructed: t.reconstructed,
            distanceSource: t.distanceSource,
          }))
          .sort(byStartTimeDesc)
      );
      if (result.error) setBinError(result.error);
    } catch (e: any) {
      setBinError(e?.message || i18n.t('copy.hooks.binAnalysisFailed'));
      setBinResult(null);
      setBinTrips([]);
    } finally {
      setLoadingBin(false);
    }
  }, []);

  const runReplay = useCallback(async (vehicleId: string, from: string, to: string) => {
    return tripAnalysisService.runReplay({ vehicleId, from, to });
  }, []);

  const reset = useCallback(() => {
    setRegisteredTrips([]);
    setBinTrips([]);
    setBinResult(null);
    setError(null);
    setBinError(null);
  }, []);

  return {
    registeredTrips,
    binTrips,
    binResult,
    loadingRegistered,
    loadingBin,
    error,
    binError,
    loadRegistered,
    loadBin,
    runReplay,
    reset,
  };
}
